import { create } from 'zustand';
import { cbLandingPageDTrackitTableDto, cbLandingPageHistoricalTableDto, ICbLandingPageDTrackitTableData, ICbLandingPageHistoricalTableData, ICbLandingPageETrackitTableData, ISuppliersTcDetails, IMaterialLedger } from '../../screens/transaction-certificates/tc-models/tc-resp-dto/cb-landingpage.res_dto';
import { cbLandingPageService, cbLandingPageServiceDTrackit, cbLandingPageServiceETrackit, } from '../../screens/transaction-certificates/tc-service/cb-landingpage.service';
import { cbLandingPageServiceV4 } from '../../screens/v4/transaction-certificates/tc-service/cb-landingpage.service';
import React from 'react';
import { Utills } from '../app-configs/utilit';
import { ICbLandingPageDTrackitTableDataV4 } from '../../screens/v4/transaction-certificates/tc-models/tc-resp-dto/cb-landingpage.res_dto';
import { useTransactionCertificateStore } from '../../store/transaction-certificates.store';
import apiService from '../service/api-service';
import { TCAppConfig } from '../app-configs/tc-app-configs';
import { formatBuyerCountryDisplay } from '../utils/buyer-country-display.utils';

export interface Pagination {
    pageIndex: number;
    pageSize: number;
}

interface SearchParams {
    teId?: string;
    tcCbLicensingCode: string;
    tcNo?: string;
    startDate?: string | undefined;
    endDate?: string | undefined;
    buyersCountry?: string;
    tcStandard?: string;
    submissionStatus?: string;
    productDetail?: string[];
    productCategory?: string[];
    outputProductDetail?: string[];
    outputProductCategory?: string[];
    rawMaterialCode?: string;
    source?: string;
    pagination: Pagination;
    rowCount?: number;
    setRowCount?: React.Dispatch<React.SetStateAction<number>>;
}

interface RecordsState {
    dTrackitData: ICbLandingPageDTrackitTableData[];
    historicalData: ICbLandingPageHistoricalTableData[];
    eTrackitData: ICbLandingPageETrackitTableData[];
    suppliersData: ISuppliersTcDetails[];
    materialLedgerData: IMaterialLedger[];
    materialLedgerCurrentBalance: number | null;
    dTrackitDataV4: ICbLandingPageDTrackitTableDataV4[];
    isLoading: boolean;
    error: Error | null;
    selectedSource: string;
    pagination: Pagination;
    hasMorePages: boolean;
    fetchRecords: (params: SearchParams) => Promise<void>;
    setSelectedSource: (source: string) => void;
    setPagination: (pagination: Pagination) => void;
    updateDTrackitV4Row: (tcId: string, updates: Partial<ICbLandingPageDTrackitTableDataV4>) => void;
}

const useRecordsStore = create<RecordsState>((set) => {
    // Private mapping functions within the store
    const mapToDTrackitTable = (record: any): ICbLandingPageDTrackitTableData => {
        Utills.cl(record)
        const latestFile = Array.isArray(record.tcUploadedFiles) && record.tcUploadedFiles.length > 0
            ? record.tcUploadedFiles[0] : null;
        return {
            tcId: record.tcId.toString() || "",
            tcNo: record.tcNo.toString() || "",
            tcVersionNumber: record.tcVersionNo || "",
            sellerName: record.sellerName || record.tcSellerInformation?.sellerName || "",
            buyerName: record.buyerName || record.tcBuyerInformation?.buyerName || "",
            buyerCountry: record.buyerCountry || record.tcBuyerInformation?.buyerCountryOrAreaDescription || record.tcBuyerInformation?.buyerCountryOrArea || record.tcBuyerInformation?.buyerCountry || "",
            tcStandard: record.tcStandard || "",
            tcNetShippingWeight: record.tcNetShippingWeight || "",
            tcDateOfIssue: record.tcDateOfIssue || "",
            tcMetadataStatus: record.tcMetadataStatus || "",
            tcCreationDate: record.tcCreationDate || "",
            tcMetadataRejectionReason: record.tcMetadataRejectionReason || "",
            tcParentMetadataStatus: record.tcParentMetadataStatus || "",
            tcUploadedFileName: latestFile?.fileName || "",
            tcPdfUploadStatus: record.tcPdfUploadStatus || "",
        };
    };

    const mapToHistoricalTable = (record: any): ICbLandingPageHistoricalTableData => {
        return {
            tcId: record.tcNo || "",
            tcVersionNumber: record.tcVersionNo || "",
            sellerName: record.sellerName || "",
            buyerName: record.buyerName || "",
            buyerCountry: record.buyer_country || "",
            tcNetShippingWeight: record.tcNetShippingWeight || "",
            tcDateOfIssue: record.tcCreationDate || ""
        };
    };

    const mapToETrackitTable = (record: any): ICbLandingPageETrackitTableData => {
        console.log(`Map Etrackit ${JSON.stringify(record)}`)
        return {
            etcId: record.etcId || "",
            etcNo: record.etcNo || "",
            etcVersionNumber: record.etcVersionNo || "",
            etcStatus: record.tcStatus || "",
            tcCbLicensingCode: record.tcCbLicensingCode || "",
            sellerName: record.sellerName || "",
            buyerName: record.buyerName || "",
            buyerCountry: record.buyersCountry || "",
            tcNetShippingWeight: record.tcNetShippingWeight || "",
            tcDateOfIssue: record.tcDateOfIssue || "",
            tcMetadataStatus: record.currentStatus || "",
            tcMetadataRejectionReason: record.RejectionReason || "",
            tcCreationDate: record.tcCreationDate || "",
            pendingDays: record.pendingDays || ""
        };
    }

    const mapToSuppliersTcDetails = (record: any): ISuppliersTcDetails => {
        const latestFile = Array.isArray(record.tcUploadedFiles) && record.tcUploadedFiles.length > 0
            ? record.tcUploadedFiles[0] : null;
        return {
            tcId: record.tcId || "",
            tcNo: record.tcNo || "",
            tcVersionNo: record.tcVersionNo || "",
            tcStatus: record.tcStatus || "",
            tcCbLicensingCode: record.tcCbLicensingCode || "",
            buyerName: record.buyerName || "",
            sellerName: record.sellerName || "",
            tcNetShippingWeight: record.tcNetShippingWeight || "",
            tcMetadataStatus: record.tcMetadataStatus || "",
            tcMetadataRejectionReason: record.tcMetadataRejectionReason || "",
            tcDateOfIssue: record.tcDateOfIssue || "",
            tcCreationDate: record.tcCreationDate || "",
            tcUploadedFileName: latestFile?.fileName || "",
            tcPdfUploadStatus: record.tcPdfUploadStatus || "",
        };
    };
    const mapToMaterialLedgerEntry = (record: any): IMaterialLedger => {
        console.log("rec", record);
        return {
            tcCbLicensingCode: record.tcCbLicensingCode || "", // Default to an empty string if undefined
            certifiedRmCode: record.certifiedRmCode || "",
            transactionType: record.transactionType || "",
            tcStatus: record?.tcStatus || "",
            inventoryType: record.inventoryType || "",
            tcEtcMdfNumber: record.tcEtcMdfNumber || "",
            tcVersionNumber: record?.tcVersionNo || "",
            sellerBuyerName: record.sellerBuyerName || "",
            transactionDate: record.transactionDate || "",
            productCategory: record.productCategory || [],
            productDetail: record.productDetail || [],
            productNumber: record.productNumber || "",
            certifiedWeightBought: record.certifiedWeightBought || 0, // Default to 0 for numeric values
            outputProductCategory: record?.outputProductCategory || "",
            outputProductDetail: record?.outputProductDetail || "",
            outputCertifiedWeightSold: record?.outputCertifiedWeightSold || "",
            rmComposition: record.rmComposition || "",
            balance: record.balance || 0,
            ledgerTransactionId: record.ledgerTransactionId || "",
            certifiedWeightSold: record.certifiedWeightSold || "", // Can be empty or defaulted to ""
            inputTcCertificateUsed: record.inputTcCertificateUsed || "",
            ledgerTransactionTimestamp: record.ledgerTransactionTimestamp ? record.ledgerTransactionTimestamp.split('T')[0] : "",
            tcId: record.tcId || null,
            atcId: record.atcId || null,
            justificationReason: record.justificationReason || "", // Map justificationReason from API
        };
    };

    // New mapping function for v4 API response
    const mapToDTrackitTableTcV4 = (record: any): ICbLandingPageDTrackitTableDataV4 => {
        Utills.cl(record)
        const uploadedFiles: Array<{ fileName: string; blobUrl?: string; uploadedAt?: string; uploadedDate?: string }> =
            Array.isArray(record.tcUploadedFiles) ? record.tcUploadedFiles : [];
        const latestFile = uploadedFiles.length > 0 ? uploadedFiles[0] : null;
        const buyerCountry = record.buyerCountry || record.tcBuyerInformation?.buyerCountryOrAreaDescription || record.tcBuyerInformation?.buyerCountryOrArea || record.tcBuyerInformation?.buyerCountry || "";
        return {
            tcId: record.tcId.toString() || "",
            tcNo: record.tcNo.toString() || "",
            tcVersionNo: record.tcVersionNo || "",
            tcStatus: record.tcStatus || "",
            sellerName: record.sellerName || record.tcSellerInformation?.sellerName || "",
            buyerName: record.buyerName || record.tcBuyerInformation?.buyerName || "",
            buyerCountry: formatBuyerCountryDisplay(buyerCountry),
            tcStandard: Array.isArray(record.tcStandard) ? record.tcStandard.join(', ') : record.tcStandard || "",
            tcNetShippingWeight: record.tcNetShippingWeight || "",
            tcDateOfIssue: record.tcDateOfIssue || "",
            // Carried on the row so the amend flow (which rebuilds the
            // landing doc from the row object) doesn't drop the issue date
            // for API-created TCs that only store tcIssueDate (86ajcj9fc).
            tcIssueDate: record.tcIssueDate || record.tcDateOfIssue || "",
            // Consumed by the PDF-upload filename validation (TcPdfUploaderModal),
            // which builds/validates the suggested filename's date segment
            // against this field instead of tcDateOfIssue.
            tcLastUpdatedDate: record.tcLastUpdatedDate || "",
            tcMetadataStatus: record.tcMetadataStatus || "",
            tcCreationDate: record.tcCreationDate || "",
            tcMetadataRejectionReason: record.tcMetadataRejectionReason || "",
            tcParentMetadataStatus: record.tcParentMetadataStatus || "",
            tcUploadedFileName: record.tcUploadedFileName || record.tcDocumentName || latestFile?.fileName || "",
            tcPdfUploadStatus: record.tcPdfUploadStatus || "",
            tcDocumentUrl: record.tcDocumentUrl || latestFile?.blobUrl || "",
            tcNoPdfUrl: record.tcNoPdfUrl || "",
            tcVersionNoPdfUrl: record.tcVersionNoPdfUrl || "",
            tcUploadedFiles: uploadedFiles,
            // Left undefined for docs created before the backend stamp so the
            // dashboard shows no source tag for them (newer TCs only).
            submittedFromUi: record.submittedFromUi,
        };
    };

    return {
        dTrackitData: [],
        historicalData: [],
        eTrackitData: [],
        suppliersData: [],
        materialLedgerData: [],
        materialLedgerCurrentBalance: null,
        dTrackitDataV4: [],
        isLoading: false,
        error: null,
        selectedSource: 'dTrackit',
        hasMorePages: false,
        pagination: {
            pageIndex: 0,
            pageSize: 10,
        },

        updateDTrackitV4Row: (tcId: string, updates: Partial<ICbLandingPageDTrackitTableDataV4>) => {
            set((state) => ({
                dTrackitDataV4: state.dTrackitDataV4.map(row =>
                    row.tcId === tcId ? { ...row, ...updates } : row
                )
            }));
        },

        setSelectedSource: (source: string) => {
            set((state) => ({
                ...state,
                selectedSource: source,
                pagination: {
                    pageIndex: 0,
                    pageSize: state.pagination.pageSize,
                    rowCount: 0
                }
            }));
        },

        setPagination: (pagination: Pagination) => {
            set((state) => ({
                ...state,
                pagination: {
                    ...state.pagination,
                    ...pagination
                }
            }));
        },

        fetchRecords: async (params: SearchParams) => {
            console.log(`SearchParams ${JSON.stringify(params.buyersCountry)}`);
            const payload = {
                tcCbLicensingCode: params.tcCbLicensingCode,
                source: params.source,
                tcNo: params.tcNo,
                limit: params.pagination.pageSize,
                offset: params.pagination.pageIndex * params.pagination.pageSize,
                startDate: params.startDate,
                endDate: params.endDate,
                buyersCountry: params.buyersCountry,
                tcStandard: params.tcStandard,
                submissionStatus: params.submissionStatus
            }
            console.log(`SearchParams payload ${JSON.stringify(payload)}`);

            try {
                // Start loading and clear the data for the selected source
                set((state) => ({
                    isLoading: true,
                    selectedSource: params.source || 'dTrackit',
                    pagination: params.pagination,
                    dTrackitData: params.source === 'dTrackit' ? [] : state.dTrackitData,
                    dTrackitDataV4: params.source === 'dTrackitV4' ? [] : state.dTrackitDataV4,
                    historicalData: params.source === 'historical' ? [] : state.historicalData,
                    eTrackitData: params.source === 'eTrackit' ? [] : state.eTrackitData,
                    materialLedgerData: params.source === 'materialLedger' ? [] : state.materialLedgerData
                }));

                if (params.source === 'dTrackit') {
                    Utills.cl("dTrackit params", params);
                    const apiResponse = await cbLandingPageService.fetchCbLandingPageServiceRecords(payload);
                    Utills.cl(apiResponse, "----------------");

                    const mappedDTrackitRecords = apiResponse.response.map(mapToDTrackitTable);
                    Utills.cl("mappedDTrackitRecords", mappedDTrackitRecords);
                    // Use has_more from backend API response
                    const hasMore = apiResponse.has_more ?? false;
                    set((state) => ({
                        dTrackitData: mappedDTrackitRecords,
                        hasMorePages: hasMore,
                        pagination: {
                            ...state.pagination,
                        },
                    }));
                } else if (params.source === 'dTrackitV4') {
                    Utills.cl("dTrackitV4 params", params);

                    const apiResponse = await cbLandingPageServiceV4.fetchCbLandingPageServiceRecords(payload);
                    Utills.cl(apiResponse, "----------------");

                    // Normalize + map records
                    const mappedDTrackitRecords = apiResponse.response.map((item: any) =>
                        mapToDTrackitTableTcV4({
                            ...item,
                            // API returns key "tcStatus " (trailing space) — read with space, trim, fall back to non-space variant
                            tcStatus: (item["tcStatus "] ?? item["tcStatus"] ?? "").toString().trim(),
                        })
                    );

                    Utills.cl("mappedDTrackitRecords", mappedDTrackitRecords);

                    // Update row count in component (for pagination UI)
                    if (params.setRowCount) params.setRowCount(apiResponse?.no_of_records ?? 0);

                    // Update Zustand store
                    set((state) => ({
                        dTrackitDataV4: mappedDTrackitRecords,
                        rowCount: apiResponse?.no_of_records ?? 0,
                        pagination: { ...state.pagination },
                    }));

                    // In the background, fetch missing buyer/seller names or weights for V4 records
                    mappedDTrackitRecords.forEach(async (row: any) => {
                        // 1. Fetch missing buyer/seller names
                        if (row.tcId && (!row.sellerName || !row.buyerName)) {
                            try {
                                const detailRes = await apiService.get<any>(
                                    TCAppConfig.GET_TC_BUYERSSELLERS_DETAILS_V4,
                                    { tcId: row.tcId }
                                );
                                const data = detailRes?.data;
                                if (data) {
                                    const sellerInfo = data.tcSellerInformation || data.sellerInformation || {};
                                    const buyerInfo = data.tcBuyerInformation || data.buyerInformation || {};
                                    
                                    const updates: Partial<ICbLandingPageDTrackitTableDataV4> = {
                                        sellerName: sellerInfo.sellerName || "",
                                        buyerName: buyerInfo.buyerName || "",
                                        buyerCountry: buyerInfo.buyerCountryOrAreaDescription || buyerInfo.buyerCountryOrArea || buyerInfo.buyerCountry || "",
                                    };
                                    
                                    // Update the row in Zustand store
                                    set((state) => ({
                                        dTrackitDataV4: state.dTrackitDataV4.map(r =>
                                            r.tcId === row.tcId ? { ...r, ...updates } : r
                                        )
                                    }));
                                }
                            } catch (err) {
                                console.error("Failed to fetch background details for tcId:", row.tcId, err);
                            }
                        }

                        // 2. Fetch missing weight
                        if (row.tcId && (row.tcNetShippingWeight === "" || row.tcNetShippingWeight === null || row.tcNetShippingWeight === undefined)) {
                            try {
                                const productRes = await apiService.get<any>(
                                    TCAppConfig.GET_LIST_PRODUCT_INFO_V4,
                                    { tcId: row.tcId, limit: 100, offset: 0 }
                                );
                                const result = productRes?.data;
                                if (result && result.response) {
                                    let responseArray: any[] = [];
                                    if (typeof result.response === "string") {
                                        try {
                                            responseArray = JSON.parse(result.response);
                                        } catch (e) {
                                            responseArray = [];
                                        }
                                    } else if (Array.isArray(result.response)) {
                                        responseArray = result.response;
                                    }

                                    if (responseArray.length > 0) {
                                        const totalWeight = responseArray.reduce((sum, item) => {
                                            const w = parseFloat(item.tcProductNetShippingWeight ?? item.tcNetShippingWeight ?? 0);
                                            return sum + (isNaN(w) ? 0 : w);
                                        }, 0);

                                        // Update the row weight in Zustand store
                                        set((state) => ({
                                            dTrackitDataV4: state.dTrackitDataV4.map(r =>
                                                r.tcId === row.tcId ? { ...r, tcNetShippingWeight: totalWeight.toString() } : r
                                            )
                                        }));
                                    }
                                }
                            } catch (err) {
                                console.error("Failed to fetch background weight for tcId:", row.tcId, err);
                            }
                        }
                    });
                }
                else if (params.source === 'historical') {
                    Utills.cl("Historical params", params);
                    Utills.cl("i am calling dTrackit service");
                    const apiResponse = await cbLandingPageServiceDTrackit.fetchCbLandingPageServiceRecords(payload);
                    Utills.cl(apiResponse, "----------------");

                    const mappedHistoricalRecords = apiResponse.response.map(mapToHistoricalTable);
                    if (params.setRowCount) params.setRowCount(apiResponse?.no_of_records);
                    set((state) => ({
                        historicalData: mappedHistoricalRecords,
                        rowCount: apiResponse.no_of_records,
                        pagination: {
                            ...state.pagination,
                        },
                    }));
                } else if (params.source === 'eTrackit') {
                    Utills.cl("eTrackit params", params);
                    const apiResponse = await cbLandingPageServiceETrackit.fetchCbLandingPageServiceRecords(payload);

                    const mappedETrackitRecords = apiResponse.response.map(mapToETrackitTable);
                    if (params.setRowCount) params.setRowCount(apiResponse?.no_of_records);
                    console.log(`Mapped Etrackit ${JSON.stringify(mappedETrackitRecords)}`)
                    Utills.cl(apiResponse.response)
                    set((state) => ({
                        eTrackitData: mappedETrackitRecords,
                        rowCount: apiResponse.no_of_records,
                        pagination: {
                            ...state.pagination,
                        },
                    }));
                }
                else if (params.source === 'suppliers') {
                    const supplierPayload = {
                        teId: "TE-01",
                        source: params.source,
                        limit: params.pagination.pageSize,
                        offset: params.pagination.pageIndex * params.pagination.pageSize,
                        startDate: params.startDate,
                        endDate: params.endDate,
                        buyersCountry: params.buyersCountry
                    }
                    const apiResponse = await cbLandingPageService.fetchSuppliersLandingPageServiceRecords(supplierPayload);

                    const mappedSuppliersRecords = apiResponse.response.map(mapToSuppliersTcDetails);
                    Utills.cl("mappedDTrackitRecords", mappedSuppliersRecords);
                    if (params.setRowCount) params.setRowCount(apiResponse?.no_of_records);
                    Utills.cl(apiResponse.response)
                    set((state) => ({
                        suppliersData: apiResponse.response,
                        rowCount: apiResponse.no_of_records,
                        pagination: {
                            ...state.pagination,
                        },
                    }));
                }
                else if (params.source === 'materialLedger') {
                    const cbLicensingCode = useTransactionCertificateStore.getState().cbLicensingCode || params.tcCbLicensingCode;

                    const materialLedgerPayload = {
                        teId: params.teId,
                        rawMaterialCode: params.rawMaterialCode,
                        cbLicensingCode,
                        limit: params.pagination.pageSize,
                        offset: params.pagination.pageIndex * params.pagination.pageSize,
                        startDate: params.startDate,
                        endDate: params.endDate,
                        ...(params.productCategory?.length && { productCategory: JSON.stringify(params.productCategory) }),
                        ...(params.productDetail?.length && { productDetail: JSON.stringify(params.productDetail) }),
                        ...(params.outputProductCategory?.length && { outputProductCategory: JSON.stringify(params.outputProductCategory) }),
                        ...(params.outputProductDetail?.length && { outputProductDetail: JSON.stringify(params.outputProductDetail) }),
                    }
                    console.log(materialLedgerPayload)
                    const apiResponse = await cbLandingPageService.fetchMaterialLedgerPageServiceRecords(materialLedgerPayload);

                    const mappedSuppliersRecords = apiResponse.response.map(mapToMaterialLedgerEntry);
                    if (params.setRowCount) params.setRowCount(apiResponse?.no_of_records);
                    Utills.cl(apiResponse.response)
                    set((state) => ({
                        materialLedgerData: mappedSuppliersRecords,
                        materialLedgerCurrentBalance: apiResponse.availableCertifiedBalance ?? null,
                        rowCount: apiResponse.no_of_records,
                        pagination: {
                            ...state.pagination,
                        },
                    }));
                }
                set({ error: null });
            } catch (err) {
                set({ error: err as Error });
                console.error("Error fetching data:", err);
            } finally {
                set({ isLoading: false });
            }
        },
    };
});

export default useRecordsStore;
